<template>
	<div ref="navRef">
		<slot name="items" :priorityLevel="priorityLevel"></slot>
		<slot name="mobile" :priorityLevel="priorityLevel"></slot>
	</div>
</template>

<script setup>
import { ref, onMounted, computed, nextTick } from 'vue'
import { onResize } from "@js/throttle.js"

const navRef = ref(null)
const priorityLevel = ref(100)
const visibleItems = ref([])

const navItems = ref(null)

const calculateItemsWidth = () => {
	if (!navRef.value || !navItems.value) return

	let totalWidth = 0
	let nonCtaCount = 0

	navItems.value.forEach((item, index) => {
		totalWidth += item.getBoundingClientRect().width
		if (!item.hasAttribute('name') || item.getAttribute('name') !== 'cta') {
			nonCtaCount++
		}
	})

	// Add gaps between non-CTA items (24px each)
	if (nonCtaCount > 1) {
		totalWidth += (nonCtaCount - 1) * 24
	}

	// Add extra gap for CTA item if it exists
	const ctaItem = navItems.value.find(item => item.getAttribute('name') === 'cta')
	if (ctaItem) {
		totalWidth += 40
	}

	return totalWidth
}

const updateVisibleItems = async () => {
	if (!navRef.value || !navItems.value) return

	const containerWidth = navRef.value.parentElement.getBoundingClientRect().width

	// Get menu button width
	priorityLevel.value = 1
	await nextTick()
	const menuButtonWidth = navRef.value.querySelector('[name="mobile"]')?.getBoundingClientRect().width || 0

	// Reset visible items
	visibleItems.value = [...navItems.value]
	priorityLevel.value = 100
	await nextTick()

	// Sort items by importance (lower number = higher priority)
	const sortedItems = [...visibleItems.value].sort((a, b) => {
		const priorityA = parseInt(a.dataset.priority) || 999
		const priorityB = parseInt(b.dataset.priority) || 999
		return priorityA - priorityB
	})

	// Calculate total width of all items
	let totalWidth = calculateItemsWidth()

	const maxTab = window.matchMedia("not all and (min-width: 60rem)");

	// If items don't fit, start hiding them
	if (containerWidth < totalWidth || maxTab.matches) {
		// Start with total width including menu button
		let currentWidth = totalWidth + menuButtonWidth + 22

		// Hide items starting from lowest priority until everything fits
		for (let i = sortedItems.length - 1; i >= 0; i--) {
			if (currentWidth <= containerWidth) {
				// Check if we have at least 2 non-CTA items visible
				const nonCtaItems = visibleItems.value.filter(item =>
					!item.hasAttribute('name') || item.getAttribute('name') !== 'cta'
				)
				if (nonCtaItems.length < 2) {
					// If less than 2 non-CTA items, hide all non-CTA items
					priorityLevel.value = 4
				}
				break
			}

			const item = sortedItems[i]
			const itemWidth = item.getBoundingClientRect().width

			// Set priority level
			priorityLevel.value = (parseInt(item.dataset.priority) || 101) - 1

			// Remove item from visible array
			visibleItems.value = [...visibleItems.value].filter(visibleItem => visibleItem !== item)

			// Subtract item width and gap from total
			currentWidth -= itemWidth
			// Subtract appropriate gap based on remaining items and CTA
			if (item.hasAttribute('name') && item.getAttribute('name') === 'cta') {
				currentWidth -= 20 // CTA gap
			} else {
				const remainingNonCta = visibleItems.value.filter(item => !item.hasAttribute('name') || item.getAttribute('name') !== 'cta').length
				if (remainingNonCta >= 1) {
					currentWidth -= 24 // Regular gap between non-CTA items
				} else {
					currentWidth -= 40 // Gap for last non-CTA item
				}
			}

		}
	}

}

onMounted(() => {
	// Get all direct a and button elements, plus those within nav elements
	navItems.value = computed(() => {
		// Get direct a and button elements
		const directElements = navRef.value?.querySelectorAll(':scope > a:not([name="mobile"]), :scope > button:not([name="mobile"])')
		// Get a and button elements within nav elements
		const navElements = navRef.value?.querySelectorAll(':scope > nav a, :scope > nav button')

		// Combine both sets of elements
		return [...directElements, ...navElements]
	}).value

	updateVisibleItems()
})

onResize(() => {
	updateVisibleItems()
});

</script>
